// Class results for IZ unit exams the period's students sat (examination-service OnlineExam_Submission),
// joined to the roster like Bài nộp. Import-free apart from types and import-free modules, for node --test.

import type { IztExamAnswer, IztExamOption, IztExamQuestion, IztExamSubmission } from '../../api/types';
import { homeworkParagraphs } from '../homework/text.ts';
import { codeOfEmail, schoolYearOf, vnDate, type RosterStudent } from '../submissions/submissions.ts';

/** izteach-admin-3.0's three làm-bài colours. */
export const STATUS_COLORS = { done: '#4BCA81', doing: '#FFC410', idle: '#F2F0F1' };
/** Rank badge blue, also izteach-admin-3.0's. */
export const RANK_BADGE_COLOR = '#0084FF';

// lms-fe's bucket vocabulary, top band first; the ramp is green to red
const SCORE_BUCKETS: { title: string; min: number; color: string }[] = [
  { title: '≥9', min: 9, color: STATUS_COLORS.done },
  { title: '8.0 đến <9.0', min: 8, color: '#10BEA7' },
  { title: '7.0 đến <8.0', min: 7, color: '#2563EB' },
  { title: '6.0 đến <7.0', min: 6, color: STATUS_COLORS.doing },
  { title: '5.0 đến <6.0', min: 5, color: '#FF7F07' },
  { title: '<5.0', min: 0, color: '#EF4444' },
];

export type AttemptStatus = 'submitted' | 'doing';

export interface ExamAttempt {
  id: string;
  code: string;
  name: string;
  examId: string;
  courseId: string;
  status: AttemptStatus;
  /** 'final' is the graded attempt; an older one turns 'done' when the student resubmits. */
  final: boolean;
  /** Out of 10, like lms-api's correct / total * 10. */
  score: number | null;
  startTime: string;
  submitTime: string;
  answers: Record<string, IztExamAnswer>;
  questionIds: string[];
}

export interface ExamSummary {
  examId: string;
  courseId: string;
  /** "Bài kiểm tra 06/10/2025": an exam carries no title of its own, and its unit is not public. */
  title: string;
  firstStart: string;
  schoolYear: string;
  /** The attempt that counts per student code. */
  byCode: Record<string, ExamAttempt>;
  submitted: number;
  doing: number;
}

export interface ExamSlice {
  title: string;
  count: number;
  color: string;
}

export interface ExamLeaderRow {
  rank: number;
  code: string;
  name: string;
  score: number;
  startTime: string;
  submitTime: string;
}

export type ExamRowStatus = 'submitted' | 'doing' | 'idle';

/** One roster student in Chi tiết; rank and score only once submitted. */
export interface ExamClassRow {
  code: string;
  name: string;
  status: ExamRowStatus;
  rank: number | null;
  score: number | null;
  startTime: string;
  submitTime: string;
}

export interface ExamOptionStat {
  key: string;
  label: string;
  text: string;
  /** Raw option html (formulas are images); sanitized where it renders. */
  html: string;
  correct: boolean;
  count: number;
}

export interface ExamQuestionStat {
  id: string;
  /** 0-based; the panel renders it as `Câu {index + 1}`. */
  index: number;
  title: string;
  stem: string;
  /** Raw stem html, else the group passage's; sanitized where it renders. */
  stemHtml: string;
  countCorrectSelections: number;
  countWrongSelections: number;
  /** Submitted attempts that left the question empty. */
  blank: number;
  options: ExamOptionStat[];
}

export interface ExamResults {
  total: number;
  submitted: number;
  doing: number;
  status: ExamSlice[];
  averageScore: number;
  distribution: ExamSlice[];
  leaders: ExamLeaderRow[];
  questions: ExamQuestionStat[];
  /** The whole roster: ranked first, then submitted unscored, Đang làm, Chưa làm, roster order within each. */
  classList: ExamClassRow[];
}

function timeOf(iso: string): number {
  const time = Date.parse(iso);
  return isNaN(time) ? 0 : time;
}

/** Null for a row the board cannot place: no id, no school email, no exam, or not started. */
export function toAttempt(row: IztExamSubmission, domain: string): ExamAttempt | null {
  const id = String(row.id || row._id || '');
  const code = codeOfEmail(row.email, domain);
  const examId = String(row.exam || '');
  const state = String(row.state || '');
  if (!id || !code || !examId) return null;
  const final = state === 'final';
  const submitted = final || (state === 'done' && Boolean(row.submitTime));
  if (!submitted && state !== 'started' && state !== 'doing') return null;
  const total = Number(row.total);
  const points = Number(row.correct);
  const score = submitted && total > 0 && !isNaN(points) ? Math.round((points / total) * 1000) / 100 : null;
  return {
    id,
    code,
    name: String(row.name || '').trim() || code,
    examId,
    courseId: String(row.course || ''),
    status: submitted ? 'submitted' : 'doing',
    final,
    score,
    startTime: String(row.startTime || ''),
    submitTime: submitted ? String(row.submitTime || '') : '',
    answers: row.answers && typeof row.answers === 'object' ? row.answers : {},
    questionIds: Array.isArray(row.questions) ? row.questions.map(String) : [],
  };
}

// the graded attempt wins, then a superseded submitted one, then work in progress; newest first within each
function outranks(a: ExamAttempt, b: ExamAttempt): boolean {
  const weight = (x: ExamAttempt) => (x.final ? 2 : x.status === 'submitted' ? 1 : 0);
  if (weight(a) !== weight(b)) return weight(a) > weight(b);
  return timeOf(a.submitTime || a.startTime) > timeOf(b.submitTime || b.startTime);
}

/** Exams this roster sat, newest first, with the attempt that counts per student. */
export function groupExams(attempts: ExamAttempt[], roster: RosterStudent[]): ExamSummary[] {
  const inRoster: Record<string, boolean> = {};
  roster.forEach((student) => { inRoster[student.code] = true; });
  const byId: Record<string, ExamSummary> = {};
  const exams: ExamSummary[] = [];
  attempts.forEach((attempt) => {
    if (!inRoster[attempt.code]) return;
    let exam = byId[attempt.examId];
    if (!exam) {
      exam = { examId: attempt.examId, courseId: attempt.courseId, title: '', firstStart: '', schoolYear: '', byCode: {}, submitted: 0, doing: 0 };
      byId[attempt.examId] = exam;
      exams.push(exam);
    }
    const previous = exam.byCode[attempt.code];
    if (!previous || outranks(attempt, previous)) exam.byCode[attempt.code] = attempt;
    if (attempt.startTime && (!exam.firstStart || timeOf(attempt.startTime) < timeOf(exam.firstStart))) exam.firstStart = attempt.startTime;
  });
  exams.forEach((exam) => {
    Object.keys(exam.byCode).forEach((code) => {
      if (exam.byCode[code].status === 'submitted') exam.submitted += 1;
      else exam.doing += 1;
    });
    exam.title = exam.firstStart ? `Bài kiểm tra ${vnDate(exam.firstStart)}` : 'Bài kiểm tra';
    exam.schoolYear = schoolYearOf(exam.firstStart);
  });
  return exams.sort((a, b) => timeOf(b.firstStart) - timeOf(a.firstStart));
}

/** exam id to the title of the unit that wraps it; a unit read needs a token, so this may stay empty. */
export function examTitlesFromUnits(units: { title?: string | null; exam?: string | null }[]): Record<string, string> {
  const titles: Record<string, string> = {};
  units.forEach((unit) => {
    const exam = unit.exam ? String(unit.exam) : '';
    const title = String(unit.title || '').trim();
    if (exam && title && !titles[exam]) titles[exam] = title;
  });
  return titles;
}

/** The unit's own title when known, else the dated label. */
/** An exam nobody in the class has attempted: the card shows 0 submitted. */
export function emptyExam(examId: string): ExamSummary {
  return { examId, courseId: '', title: 'Bài kiểm tra', firstStart: '', schoolYear: '', byCode: {}, submitted: 0, doing: 0 };
}

export type ExamCardView = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; exam: ExamSummary };

/** The results card for one exam: its attempts once found, else the load's state, else an empty exam. */
export function examCardView(exams: ExamSummary[], examId: string, load: { loading: boolean; error: string | null }): ExamCardView {
  const found = exams.filter((exam) => exam.examId === examId)[0];
  if (found) return { kind: 'ready', exam: found };
  if (load.loading) return { kind: 'loading' };
  if (load.error) return { kind: 'error', message: load.error };
  return { kind: 'ready', exam: emptyExam(examId) };
}

export function examDisplayTitle(exam: Pick<ExamSummary, 'examId' | 'title'>, titles: Record<string, string>): string {
  return titles[exam.examId] || exam.title;
}

/** The exact formula IZTeach uses on the "sai nhiều nhất" bars. */
export function wrongPercent(stat: Pick<ExamQuestionStat, 'countWrongSelections' | 'countCorrectSelections'>): number {
  const answered = stat.countWrongSelections + stat.countCorrectSelections;
  return answered === 0 ? 0 : Math.round((stat.countWrongSelections / answered) * 100);
}

/** Right answers over answered, as a whole percent. */
export function correctPercent(stat: Pick<ExamQuestionStat, 'countWrongSelections' | 'countCorrectSelections'>): number {
  const answered = stat.countWrongSelections + stat.countCorrectSelections;
  return answered === 0 ? 0 : Math.round((stat.countCorrectSelections / answered) * 100);
}

export function topWrongQuestions(questions: ExamQuestionStat[], take = 3): ExamQuestionStat[] {
  // board order breaks a tie
  return questions
    .filter((question) => question.countWrongSelections > 0)
    .slice()
    .sort((a, b) => (b.countWrongSelections - a.countWrongSelections) || (a.index - b.index))
    .slice(0, take);
}

export function durationSeconds(row: Pick<ExamLeaderRow, 'startTime' | 'submitTime'>): number {
  return Math.max(0, Math.round((timeOf(row.submitTime) - timeOf(row.startTime)) / 1000));
}

/** `4 phút 12 giây`; an hour-long exam reads in minutes. */
export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes === 0) return `${rest} giây`;
  if (rest === 0) return `${minutes} phút`;
  return `${minutes} phút ${rest} giây`;
}

/** `Câu 2: ...`, clipped so a long stem cannot push the bar off the panel. */
export function questionLabel(index: number, text: string, limit = 70): string {
  const full = text ? `Câu ${index + 1}: ${text}` : `Câu ${index + 1}`;
  return full.length <= limit ? full : `${full.slice(0, limit - 1).trim()}…`;
}

export function formatScore(score: number): string {
  return String(Math.round(score * 100) / 100).replace('.', ',');
}

const TYPE_HINT: Record<string, string> = {
  'multiple-choice-pdf': 'Trắc nghiệm theo đề PDF',
  'single-choice-pdf': 'Trắc nghiệm theo đề PDF',
  'fill-blank': 'Điền khuyết',
  'fill-blank-child': 'Điền khuyết',
};

/** A group is a shared passage, not a question: students answer its children. */
function isGroup(question: IztExamQuestion): boolean {
  return question.type === 'group' || (Array.isArray(question.questions) && question.questions.length > 0);
}

function plain(html: string | null | undefined): string {
  return homeworkParagraphs(String(html || '')).join(' ');
}

function optionsOf(question: IztExamQuestion | undefined): { key: string; label: string; text: string; html: string }[] {
  const raw = question && question.answers;
  if (!raw || typeof raw !== 'object') return [];
  const entries: IztExamOption[] = Array.isArray(raw) ? raw : Object.keys(raw).map((key) => ({ key, ...(raw as Record<string, IztExamOption>)[key] }));
  return entries.map((option, i) => {
    const key = String(option.key || option.id || '');
    const label = String(option.label || String.fromCharCode(65 + i));
    const text = plain(option.value);
    return { key, label, text: text === label ? '' : text, html: String(option.value || '') };
  }).filter((option) => option.key);
}

/** Question order: the exam's own index, then any question only an attempt names. */
function questionOrder(questions: IztExamQuestion[], attempts: ExamAttempt[]): string[] {
  const ids = questions
    .filter((question) => !isGroup(question))
    .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0))
    .map((question) => String(question.id || question._id || ''))
    .filter(Boolean);
  attempts.forEach((attempt) => {
    attempt.questionIds.concat(Object.keys(attempt.answers)).forEach((id) => { if (ids.indexOf(id) < 0) ids.push(id); });
  });
  return ids;
}

// multiple choice stores arrays, single choice one string; a fill-in's text never matches an option key
function correctKeys(answer: IztExamAnswer | undefined): string[] {
  const value = answer && answer.correct;
  if (Array.isArray(value)) return value.map(String);
  return typeof value === 'string' && value ? [value] : [];
}

function pickedKeys(answer: IztExamAnswer | undefined): string[] {
  if (!answer) return [];
  if (Array.isArray(answer.answerIds)) return answer.answerIds.map(String);
  return typeof answer.answerId === 'string' && answer.answerId ? [answer.answerId] : [];
}

/** Questions by id, and each group child's passage html. */
function questionIndex(questions: IztExamQuestion[]): { byId: Record<string, IztExamQuestion>; passageOf: Record<string, string> } {
  const byId: Record<string, IztExamQuestion> = {};
  const passageOf: Record<string, string> = {};
  questions.forEach((question) => {
    byId[String(question.id || question._id || '')] = question;
    if (isGroup(question)) (question.questions || []).forEach((child) => { passageOf[String(child)] = String(question.html || ''); });
  });
  return { byId, passageOf };
}

export type AnswerVerdict = 'correct' | 'wrong' | 'blank';

export interface SheetOption {
  key: string;
  label: string;
  html: string;
  picked: boolean;
  correct: boolean;
}

/** One question of one student's attempt. */
export interface SheetRow {
  id: string;
  index: number;
  stemHtml: string;
  /** Type hint when the stem has no text (a PDF-sheet question). */
  hint: string;
  verdict: AnswerVerdict;
  options: SheetOption[];
  /** What the student typed on a fill-in. */
  typed: string;
  /** The right text of a fill-in, when the submission carries it. */
  correctText: string;
}

/** One attempt question by question, in exam order: what was picked or typed against the key. */
export function studentSheet(attempt: ExamAttempt, questions: IztExamQuestion[]): SheetRow[] {
  const { byId, passageOf } = questionIndex(questions);
  return questionOrder(questions, [attempt]).map((id, index) => {
    const question = byId[id];
    const answer = attempt.answers[id];
    const picked = pickedKeys(answer);
    const right = correctKeys(answer);
    const typed = answer && typeof answer.answer === 'string' ? answer.answer.trim() : '';
    const verdict: AnswerVerdict = !answer || (picked.length === 0 && !typed) ? 'blank' : answer.isCorrect ? 'correct' : 'wrong';
    const options = optionsOf(question).map((option) => ({ key: option.key, label: option.label, html: option.html, picked: picked.indexOf(option.key) >= 0, correct: right.indexOf(option.key) >= 0 }));
    // a fill-in's key is text, never an option key
    const correctText = options.length === 0 ? right.join(' / ') : '';
    const stemHtml = (question ? String(question.html || '').trim() : '') || passageOf[id] || '';
    const hint = question ? TYPE_HINT[String(question.type || '')] || '' : '';
    return { id, index, stemHtml, hint, verdict, options, typed, correctText };
  });
}

/** Đúng / Sai / Bỏ trống tallies of a sheet. */
export function sheetTally(rows: SheetRow[]): Record<AnswerVerdict, number> {
  const tally: Record<AnswerVerdict, number> = { correct: 0, wrong: 0, blank: 0 };
  rows.forEach((row) => { tally[row.verdict] += 1; });
  return tally;
}

export function buildExamResults(exam: ExamSummary, roster: RosterStudent[], questions: IztExamQuestion[]): ExamResults {
  const submittedAttempts: ExamAttempt[] = [];
  const unranked: ExamClassRow[] = [];
  roster.forEach((student) => {
    const attempt = exam.byCode[student.code];
    if (attempt && attempt.status === 'submitted') submittedAttempts.push(attempt);
    if (attempt && attempt.status === 'submitted' && attempt.score !== null) return;
    const status: ExamRowStatus = attempt ? attempt.status : 'idle';
    unranked.push({ code: student.code, name: student.name, status, rank: null, score: null, startTime: attempt ? attempt.startTime : '', submitTime: attempt ? attempt.submitTime : '' });
  });
  const total = roster.length;
  const submitted = submittedAttempts.length;
  const doing = unranked.filter((row) => row.status === 'doing').length;
  const statusOrder: Record<ExamRowStatus, number> = { submitted: 0, doing: 1, idle: 2 };
  // stable by roster order within each status
  const tail = unranked.map((row, at) => ({ row, at })).sort((a, b) => (statusOrder[a.row.status] - statusOrder[b.row.status]) || (a.at - b.at)).map((item) => item.row);
  const nameOf: Record<string, string> = {};
  roster.forEach((student) => { nameOf[student.code] = student.name; });

  const scored = submittedAttempts.filter((attempt) => attempt.score !== null);
  const leaders: ExamLeaderRow[] = scored
    .map((attempt) => ({ rank: 0, code: attempt.code, name: nameOf[attempt.code] || attempt.name, score: attempt.score as number, startTime: attempt.startTime, submitTime: attempt.submitTime }))
    .sort((a, b) => (b.score - a.score) || (durationSeconds(a) - durationSeconds(b)) || a.name.localeCompare(b.name));
  leaders.forEach((row, i) => { row.rank = i + 1; });
  const averageScore = scored.length === 0 ? 0 : Math.round((scored.reduce((sum, a) => sum + (a.score as number), 0) / scored.length) * 10) / 10;
  const distribution = SCORE_BUCKETS.map((bucket, i) => {
    const ceiling = i === 0 ? Number.POSITIVE_INFINITY : SCORE_BUCKETS[i - 1].min;
    return { title: bucket.title, count: scored.filter((a) => (a.score as number) >= bucket.min && (a.score as number) < ceiling).length, color: bucket.color };
  });

  const { byId, passageOf } = questionIndex(questions);
  const stats: ExamQuestionStat[] = questionOrder(questions, submittedAttempts).map((id, index) => {
    const question = byId[id];
    const options = optionsOf(question).map((option) => ({ ...option, correct: false, count: 0 }));
    let countCorrectSelections = 0;
    let countWrongSelections = 0;
    let blank = 0;
    submittedAttempts.forEach((attempt) => {
      const answer = attempt.answers[id];
      const picked = pickedKeys(answer);
      const typed = answer && typeof answer.answer === 'string' ? answer.answer.trim() : '';
      if (!answer || (picked.length === 0 && !typed)) { blank += 1; return; }
      if (answer.isCorrect) countCorrectSelections += 1;
      else countWrongSelections += 1;
      const right = correctKeys(answer);
      options.forEach((option) => {
        if (picked.indexOf(option.key) >= 0) option.count += 1;
        if (right.indexOf(option.key) >= 0) option.correct = true;
      });
    });
    const stem = (question ? plain(question.html) : '') || plain(passageOf[id]) || '';
    const hint = question ? TYPE_HINT[String(question.type || '')] || '' : '';
    const stemHtml = (question ? String(question.html || '').trim() : '') || passageOf[id] || '';
    return { id, index, title: stem ? questionLabel(index, stem) : `Câu ${index + 1}${hint ? ` (${hint})` : ''}`, stem: stem || hint, stemHtml, countCorrectSelections, countWrongSelections, blank, options };
  });

  return {
    total,
    submitted,
    doing,
    status: [
      { title: 'Đã nộp', count: submitted, color: STATUS_COLORS.done },
      { title: 'Đang làm', count: doing, color: STATUS_COLORS.doing },
      { title: 'Chưa làm', count: total - submitted - doing, color: STATUS_COLORS.idle },
    ],
    averageScore,
    distribution,
    leaders,
    questions: stats,
    classList: leaders.map((row): ExamClassRow => ({ code: row.code, name: row.name, status: 'submitted', rank: row.rank, score: row.score, startTime: row.startTime, submitTime: row.submitTime })).concat(tail),
  };
}
